import React, { useState } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import {
  Shield,
  Lock,
  Globe2,
  FileText,
  Clock,
  UserCheck,
  ChevronDown,
  ChevronUp,
  ArrowUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

export default function Privacy() {
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const sections = [
    { id: "who-we-are", label: "1. Who We Are" },
    { id: "data-we-collect", label: "2. Data We Collect" },
    { id: "how-we-use-data", label: "3. How We Use Data" },
    { id: "legal-basis", label: "4. Legal Basis (PDPL)" },
    { id: "sharing-third-parties", label: "5. Sharing & Third Parties" },
    { id: "international-transfers", label: "6. International Transfers" },
    { id: "data-retention", label: "7. Data Retention" },
    { id: "cookies-tracking", label: "8. Cookies & Tracking" },
    { id: "security", label: "9. Security Measures" },
    { id: "your-rights", label: "10. Your Rights" },
    { id: "children", label: "11. Children's Privacy" },
    { id: "changes", label: "12. Changes to this Policy" },
    { id: "contact", label: "13. Contact Us" },
  ];

  const handleScrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const yOffset = -80;
    const y = el.getBoundingClientRect().top + window.scrollY + yOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <div className="mx-auto w-full max-w-5xl px-4 py-10 md:py-12">
        {/* Header */}
        <header className="mb-8 flex flex-col gap-4 border-b border-border pb-6 md:flex-row md:items-center md:justify-between">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
              <Shield className="h-3.5 w-3.5" />
              <span>Privacy Policy</span>
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
              Privacy Policy – Shattara AI
            </h1>
            <p className="max-w-2xl text-sm text-muted-foreground">
              How Shattara AI for Information Technology collects, uses, and
              protects your personal data in line with the laws of the Kingdom
              of Saudi Arabia (PDPL).
            </p>
          </div>

          <div className="space-y-1 text-sm text-muted-foreground md:text-right">
            <p>
              <span className="font-medium text-foreground">Last Updated:</span>{" "}
              <span>January 15, 2025</span>
            </p>
            <p>
              <span className="font-medium text-foreground">
                Effective Date:
              </span>{" "}
              <span>January 15, 2025</span>
            </p>
          </div>
        </header>

        {/* Quick Summary - Desktop */}
        <section className="mb-8 hidden md:block">
          <div className="rounded-xl border border-border bg-muted/40 px-6 py-4">
            <div className="mb-3 flex items-center gap-2">
              <Lock className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-foreground">
                Privacy at a Glance
              </span>
            </div>
            <div className="grid gap-2 text-sm text-muted-foreground md:grid-cols-2">
              <p>• We collect data to run the platform and improve your study experience.</p>
              <p>• You own your uploaded content; we only process it to provide the Services.</p>
              <p>• We do not sell your personal data to third parties.</p>
              <p>• You can request access, correction, or deletion of your personal data.</p>
              <p className="md:col-span-2">• Some data may be stored or processed outside KSA with appropriate safeguards.</p>
            </div>
          </div>
        </section>

        {/* Quick Summary - Mobile Collapsible */}
        <section className="mb-8 md:hidden">
          <Collapsible open={summaryOpen} onOpenChange={setSummaryOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex w-full items-center justify-between rounded-xl border border-border bg-muted/40 px-4 py-3 text-left transition hover:bg-muted"
              >
                <div className="flex items-center gap-2">
                  <Lock className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">
                    Privacy at a Glance
                  </span>
                </div>
                {summaryOpen ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-3 space-y-2 rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
                <p>• We collect data to run the platform and improve your study experience.</p>
                <p>• You own your uploaded content; we only process it to provide the Services.</p>
                <p>• We do not sell your personal data to third parties.</p>
                <p>• You can request access, correction, or deletion of your personal data.</p>
                <p>• Some data may be stored or processed outside KSA with appropriate safeguards.</p>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </section>

        {/* Layout: TOC + Content */}
        <div className="flex flex-col gap-8 md:flex-row">
          {/* Table of Contents */}
          <aside className="w-full md:w-64 md:flex-shrink-0">
            <div className="sticky top-20 rounded-xl border border-border bg-card p-4 text-sm">
              <div className="mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  On this page
                </h2>
              </div>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => handleScrollTo(section.id)}
                    className="block w-full rounded-md px-2 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    {section.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 space-y-8 text-sm leading-relaxed text-foreground">
            {/* 1. Who We Are */}
            <section id="who-we-are" className="scroll-m-24 space-y-3">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <Shield className="h-4 w-4 text-primary" />
                1. Who We Are
              </h2>
              <p>
                This Privacy Policy ("Policy") explains how Shattara AI for
                Information Technology ("Shattara AI", "we", "us", "our") collects,
                uses, discloses, and protects personal data of users ("you") of
                our educational platform and related AI-powered Services in the
                Kingdom of Saudi Arabia.
              </p>
              <p>
                Shattara AI for Information Technology acts as the{" "}
                <span className="font-medium">data controller</span> for personal
                data processed through the Services.
              </p>
            </section>

            {/* 2. Data We Collect */}
            <section id="data-we-collect" className="scroll-m-24 space-y-3">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <FileText className="h-4 w-4 text-primary" />
                2. Data We Collect
              </h2>
              <p>We may collect the following categories of personal data:</p>
              <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
                <li>
                  <span className="font-medium text-foreground">Account & identity data</span>{" "}
                  (name, email, username, password, date of birth, region).
                </li>
                <li>
                  <span className="font-medium text-foreground">Contact data</span> (mobile
                  number, address if provided, communication preferences).
                </li>
                <li>
                  <span className="font-medium text-foreground">Content & usage data</span>{" "}
                  (documents you upload, pasted URLs or text, recordings and
                  transcripts, interactions with AI tutor/chat, study analytics).
                </li>
                <li>
                  <span className="font-medium text-foreground">Technical & device data</span>{" "}
                  (IP address, browser, device identifiers, OS, logs).
                </li>
                <li>
                  <span className="font-medium text-foreground">Payment & subscription data</span>{" "}
                  (tokenised payment method from the gateway, billing details,
                  subscription plan, transaction history).
                </li>
                <li>
                  <span className="font-medium text-foreground">
                    Cookies & similar technologies
                  </span>{" "}
                  used to recognise your session, preferences and for analytics.
                </li>
              </ul>
            </section>

            {/* 3. How We Use Data */}
            <section id="how-we-use-data" className="scroll-m-24 space-y-3">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <Clock className="h-4 w-4 text-primary" />
                3. How We Use Data
              </h2>
              <p>We use your personal data to:</p>
              <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
                <li>Provide, operate, and maintain the Shattara AI Services.</li>
                <li>
                  Enable uploads, transcription, AI tutor chat, flashcards,
                  quizzes, exams, and study analytics.
                </li>
                <li>
                  Manage your account, authenticate you, and secure your access.
                </li>
                <li>
                  Process payments, manage subscriptions, and handle billing.
                </li>
                <li>
                  Monitor usage to detect abuse, misuse, or academic integrity
                  violations.
                </li>
                <li>
                  Improve our AI models and educational features (using de-identified
                  or aggregated data where possible).
                </li>
                <li>Comply with legal obligations and resolve disputes.</li>
              </ul>
            </section>

            {/* 4. Legal Basis (PDPL) */}
            <section id="legal-basis" className="scroll-m-24 space-y-3">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <Shield className="h-4 w-4 text-primary" />
                4. Legal Basis (PDPL)
              </h2>
              <p>
                Under the Saudi Personal Data Protection Law (PDPL), we rely on
                one or more of the following legal bases:
              </p>
              <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
                <li>Your consent for specific processing activities.</li>
                <li>
                  The need to perform our contract with you (for example, your
                  subscription).
                </li>
                <li>
                  Compliance with legal obligations (such as record-keeping and
                  accounting rules).
                </li>
                <li>
                  Our legitimate interests in securing and improving the platform,
                  provided these interests do not override your rights and
                  freedoms.
                </li>
              </ul>
            </section>

            {/* 5. Sharing & Third Parties */}
            <section
              id="sharing-third-parties"
              className="scroll-m-24 space-y-3"
            >
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <Globe2 className="h-4 w-4 text-primary" />
                5. Sharing & Third Parties
              </h2>
              <p>
                We do <span className="font-medium">not</span> sell your personal
                data. We may share data with:
              </p>
              <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
                <li>
                  Service providers (e.g., hosting, cloud, transcription, payment
                  gateways) under contracts that respect PDPL requirements.
                </li>
                <li>
                  Governmental or regulatory authorities where required by Saudi
                  law or a binding order.
                </li>
                <li>
                  Other parties with your explicit consent or instructions (e.g.,
                  if you choose to share achievements).
                </li>
                <li>
                  Potential buyers or investors in case of a merger or acquisition,
                  under appropriate confidentiality and data-protection safeguards.
                </li>
              </ul>
            </section>

            {/* 6. International Transfers */}
            <section
              id="international-transfers"
              className="scroll-m-24 space-y-3"
            >
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <Globe2 className="h-4 w-4 text-primary" />
                6. International Transfers
              </h2>
              <p>
                If personal data is transferred outside the Kingdom of Saudi
                Arabia, we will ensure that:
              </p>
              <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
                <li>
                  The destination provides a level of protection not lower than
                  that in KSA; and
                </li>
                <li>
                  Appropriate contractual safeguards and PDPL-compliant mechanisms
                  are in place.
                </li>
              </ul>
            </section>

            {/* 7. Data Retention */}
            <section id="data-retention" className="scroll-m-24 space-y-3">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <Clock className="h-4 w-4 text-primary" />
                7. Data Retention
              </h2>
              <p>
                We retain your personal data only for as long as necessary to
                fulfil the purposes described in this Policy, or as required by
                Saudi law, whichever is longer. When data is no longer needed, we
                will securely delete or anonymise it.
              </p>
            </section>

            {/* 8. Cookies & Tracking */}
            <section id="cookies-tracking" className="scroll-m-24 space-y-3">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <FileText className="h-4 w-4 text-primary" />
                8. Cookies & Tracking
              </h2>
              <p>
                We may use cookies and similar technologies to recognise your
                browser, remember preferences, secure your session, and analyse
                how the platform is used.
              </p>
              <p>
                You can control cookies through your browser settings, but some
                features of the Services may not function properly if cookies are
                disabled.
              </p>
            </section>

            {/* 9. Security Measures */}
            <section id="security" className="scroll-m-24 space-y-3">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <Lock className="h-4 w-4 text-primary" />
                9. Security Measures
              </h2>
              <p>
                We implement technical and organisational measures to protect
                personal data against unauthorised access, loss, misuse, or
                disclosure. These measures may include encryption, access
                controls, secure infrastructure, and monitoring.
              </p>
            </section>

            {/* 10. Your Rights */}
            <section id="your-rights" className="scroll-m-24 space-y-3">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <UserCheck className="h-4 w-4 text-primary" />
                10. Your Rights
              </h2>
              <p>
                Subject to the PDPL and applicable regulations, you may have the
                right to:
              </p>
              <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
                <li>Be informed about how your personal data is processed.</li>
                <li>Request access to your personal data and obtain a copy.</li>
                <li>Request correction of inaccurate or incomplete data.</li>
                <li>
                  Request deletion or destruction of personal data where it is no
                  longer required.
                </li>
                <li>
                  Withdraw consent or object to certain processing, where
                  applicable.
                </li>
              </ul>
              <p>
                To exercise your rights, contact us using the details in the{" "}
                <button
                  type="button"
                  onClick={() => handleScrollTo("contact")}
                  className="text-primary underline underline-offset-4 transition-colors hover:text-primary/80"
                >
                  Contact Us
                </button>{" "}
                section.
              </p>
            </section>

            {/* 11. Children's Privacy */}
            <section id="children" className="scroll-m-24 space-y-3">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <Shield className="h-4 w-4 text-primary" />
                11. Children's Privacy
              </h2>
              <p>
                If you are under 13, you may not use the Services without verified
                parental consent. If you are aged 13–17, you must have permission
                from a parent or legal guardian. We encourage parents and
                guardians to actively supervise their children's use of Shattara
                AI.
              </p>
              <p>
                If we learn that we have collected personal data from a child
                contrary to applicable law, we will take steps to delete it.
              </p>
            </section>

            {/* 12. Changes to this Policy */}
            <section id="changes" className="scroll-m-24 space-y-3">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <Clock className="h-4 w-4 text-primary" />
                12. Changes to this Policy
              </h2>
              <p>
                We may update this Privacy Policy from time to time. When we make
                significant changes, we will notify you via the Services or by
                email and adjust the "Last Updated" date above.
                Continued use of the Services after changes become effective
                constitutes your acceptance of the updated Policy.
              </p>
            </section>

            {/* 13. Contact Us */}
            <section id="contact" className="scroll-m-24 space-y-3">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <Globe2 className="h-4 w-4 text-primary" />
                13. Contact Us
              </h2>
              <p>
                If you have questions, requests, or complaints about this Privacy
                Policy or how we handle personal data, you can contact us at:
              </p>
              <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
                <li>
                  <span className="font-medium text-foreground">Email:</span> hello@shattaraai.com
                </li>
                <li>
                  <span className="font-medium text-foreground">Address:</span> Madinah Road, Tahlia district - 23331 Jeddah, Saudi Arabia
                </li>
              </ul>
            </section>

            {/* Footer note */}
            <div className="mt-10 border-t border-border pt-4 text-xs text-muted-foreground">
              © {new Date().getFullYear()} Shattara AI for Information
              Technology. All rights reserved.
            </div>
          </main>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <Button
          onClick={scrollToTop}
          size="icon"
          className="fixed bottom-8 right-8 h-10 w-10 rounded-full shadow-lg transition-opacity hover:opacity-90"
          aria-label="Back to top"
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
      )}

      <Footer />
    </div>
  );
}
